import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Table from 'src/components/Table';
import { getPartnerClients } from 'src/redux/modules/deployment-center/actions';
import { match } from 'ts-pattern';
import { columns } from './config';
import { Wrapper } from './PartnerQueueClient.style';
import ControlBar from '../components/ControlBar';

const normalizeFilters = filters => {
  return Object.keys(filters).reduce((prev, key) => {
    if (key === 'app' && filters[key] === 'any') {
      return prev;
    }
    if (key === 'companyType' && filters[key]?.name) {
      return { ...prev, companyType: filters[key].name };
    }
    return { ...prev, [key]: filters[key] };
  }, {});
};

const mapSortOptionToQueryParam = sortOption =>
  match(sortOption)
    .with('rating', () => 'rating')
    .with('teamsUsage', () => '-teams_usage')
    .with('requests', () => '-thread_usage')
    .with('name', () => 'name')
    .otherwise(() => 'rating');

const PartnerQueueClient = ({
  getPartnerClients,
  clients,
  isFetchClients,
  errorMessage,
  history,
  location
}) => {
  const [filters, setFilters] = useState({
    state: 'any'
  });
  const [sortOption, setSortOption] = useState('rating');

  const queryStringRef = useRef('');

  const handleGetPartnerClients = useCallback(
    params => {
      const { queryString = '' } = params;
      const urlParams = new URLSearchParams(queryString);
      const normalizedFilters = normalizeFilters(filters);

      Object.keys(normalizedFilters).forEach(key => {
        if (normalizedFilters[key]) {
          urlParams.set(key, normalizedFilters[key]);
        }
      });

      const sortParam = mapSortOptionToQueryParam(sortOption);
      urlParams.set('sort', sortParam);

      getPartnerClients({ ...params, queryString: urlParams.toString() });

      queryStringRef.current = urlParams.toString();
    },
    [filters, sortOption, getPartnerClients]
  );

  useEffect(() => {
    handleGetPartnerClients({ queryString: queryStringRef.current });
  }, [filters, sortOption]);

  return (
    <Wrapper>
      <ControlBar
        filters={filters}
        onFilterChange={setFilters}
        sortOption={sortOption}
        onSortOptionChange={setSortOption}
      />
      <Table
        getData={handleGetPartnerClients}
        isLoading={isFetchClients}
        error={errorMessage}
        columns={columns()}
        onRowClick={row => {
          history.push({
            pathname: `/dashboard/clients/companies/${row.company_id}/connect`,
            state: { companyName: row.name, appId: row.company_id, search: location.search }
          });
        }}
        rows={clients}
      />
    </Wrapper>
  );
};

PartnerQueueClient.propTypes = {
  getPartnerClients: PropTypes.func.isRequired,
  clients: PropTypes.objectOf(PropTypes.any),
  isFetchClients: PropTypes.bool,
  errorMessage: PropTypes.string,
  history: PropTypes.objectOf(PropTypes.any),
  location: PropTypes.objectOf(PropTypes.any)
};

PartnerQueueClient.defaultProps = {
  clients: {},
  isFetchClients: false,
  errorMessage: '',
  history: {},
  location: {}
};

const mapStateToProps = state => ({
  clients: state.deploymentCenterReducer.clients,
  isFetchClients: state.deploymentCenterReducer.isFetchClients,
  errorMessage: state.deploymentCenterReducer.errorMessage
});

const mapDispatchToProps = dispatch => ({
  getPartnerClients: params => dispatch(getPartnerClients(params))
});

export default connect(mapStateToProps, mapDispatchToProps)(PartnerQueueClient);
